import { useEffect } from 'react';
import { COMPONENT_FORM_CONFIGS } from '../componentFormConfigs';
import { useComponentForm } from '../useComponentForm';
import { FormSubtab, handleTabToNext } from '../formKit';
import { deriveSeasonValue } from '../cmsDoc';
import { ComponentSeasonBlock } from '../SeasonGroup';
import {
  AirStreamFlow,
  BlankOffSlot,
  FormHeading,
  HeadingChrome,
  MBareInput,
  MBareSelect,
  PerformanceCommon,
  PSelect,
  RequiredFieldNote,
} from './formCommon';
import type { ComponentFormProps } from './ComponentFormHost';

/**
 * Phase 6e2a — ELIMINATOR form, re-cut byte-faithful to
 * components/eliminator/tmpl/form.tmpl (verify round 1: subtab census
 * properties/performance/frame only, paired rows, required labels, chrome
 * vis-eliminator, no rating block). Type wiring (parity 321):
 *  - onSelectChange(eliminator) → frame filter1 {eliminator_id};
 *  - init + change:bank_wide/bank_high → eliminator filter1
 *    {width_nominal_value: bank_wide×24 || null, ...} (legacy formula).
 */
const config = COMPONENT_FORM_CONFIGS.eliminator;

const EliminatorForm = ({ row, section, section2, cms }: ComponentFormProps) => {
  const api = useComponentForm({
    config,
    row,
    section,
    section2,
    firstSubtab: 'properties',
  });
  const { model } = api;

  useEffect(() => {
    if (!model) return undefined;
    const elim = api.catalogueFields.master_eliminator_id;
    const frame = api.catalogueFields.master_frame_id;
    if (!elim || !frame) return undefined;

    // Legacy catalogue_select_field.js:455-463 — a checkbox params field with
    // no stored value seeds `overrides_from_DOM[field] = false`, which the
    // init pass pushes through updateFilter (:628-630). So the eliminator
    // catalogue is queried with `vert_sides_gaskets=false` from first paint
    // (dev reads 8/16); omitting the param returns the gasketed rows too
    // (dev3 read 16/16, with the `_vertgaskets` SPNs in the smart list).
    if (elim.paramValue('vert_sides_gaskets') == null) {
      elim.updateFilter({ vert_sides_gaskets: false });
    }

    const applyBankFilter = () => {
      // Legacy: bank_wide * 24 || null (null/0 collapses to null).
      elim.resetFilter(
        {
          width_nominal_value:
            (Number(model.get('bank_wide')) || 0) * 24 || null,
          height_nominal_value:
            (Number(model.get('bank_high')) || 0) * 24 || null,
        },
        false,
        1
      );
    };
    elim.onSelectChange = () => {
      frame.resetFilter({ eliminator_id: model.get('master_eliminator_id') }, false, 1);
    };
    applyBankFilter();
    return model.onChange((changed) => {
      if ('bank_wide' in changed || 'bank_high' in changed) applyBankFilter();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, api.catalogueFields.master_eliminator_id, api.catalogueFields.master_frame_id]);

  if (api.loading || !model) {
    return <div className="preloader dotted-preloader active" />;
  }
  const sub = (name: string) => !!api.subtabs[name];
  const elimEngine = api.catalogueFields.master_eliminator_id;

  const sizeAndQty = (
    Array.isArray(model.get('size_and_qty'))
      ? (model.get('size_and_qty') as Array<Record<string, unknown>>)
      : []
  ) as Array<{ id: number; wide_high?: string; bank_wide?: unknown; bank_high?: unknown }>;
  const matchedSize = sizeAndQty.find(
    (o) =>
      // eslint-disable-next-line eqeqeq
      o.bank_wide == model.get('bank_wide') && o.bank_high == model.get('bank_high')
  );

  return (
    <>
      <FormHeading
        title="Eliminator"
        api={api}
        wrapperClass="component-form-heading clearfix"
        chrome={[
          {
            field: config.catalogueFields[0],
            visClass: 'vis-eliminator',
            searchResultClass: 'search-result',
          },
        ]}
      />
      <form onKeyDown={handleTabToNext}>
        <ul className="inputs-group">
          <FormSubtab type="eliminator" cid={row.id} name="properties" label="Properties" labelHidden open={sub('properties')} onToggle={api.toggleSubtab} sublistClass="max-height-auto">
            <PSelect api={api} target="master_eliminator_id" name="mfg_id" label="Manufacturer " required cmsPath="manufacturer" />
            <PSelect api={api} target="master_eliminator_id" name="type_id" label="Type " required cmsPath="types" />
            <PSelect api={api} target="master_eliminator_id" name="series_id" label="Series " required cmsPath="series" labelKey="series" />
            <PSelect api={api} target="master_eliminator_id" name="depth_nominal_id" label="Nominal Depth " required unit="in" cmsPath="nominal_depth" labelKey="value" extraClass="long_unit" />
            <PSelect api={api} target="master_eliminator_id" name="mtl" label="Material " required unit="in" cmsPath="mtl" labelKey="mtl" valueKey="mtl" extraClass="long_unit" />
            {/* vert_sides_gaskets is a PARAMS checkbox (eliminator_params_field). */}
            <li>
              <div>
                <input
                  type="checkbox"
                  name="vert_sides_gaskets"
                  className="eliminator_params_field"
                  checked={!!elimEngine?.paramValue('vert_sides_gaskets')}
                  onChange={(e) =>
                    api.commitParamsField(
                      'master_eliminator_id',
                      'vert_sides_gaskets',
                      e.target.checked
                    )
                  }
                />{' '}
                <span className="label required">Vertical Side Gaskets</span>
              </div>
            </li>
            <li>
              <div className="single-line">
                <MBareSelect api={api} name="frame_loading_id" cmsPath="frame.loading" extraClass="long_unit" />{' '}
                <span className="label required">Loading </span>
              </div>
            </li>
            <li>
              <div>
                <span className="unit">in</span>{' '}
                <MBareInput api={api} name="distance_from_section_ups" />{' '}
                <span className="label">From Section Upstream</span>
              </div>
            </li>
            <li className="include-checkbox">
              <div className="checkboxes-group">
                <span className="unit">in</span>{' '}
                <MBareInput
                  api={api}
                  name="height_from_floor"
                  extraClass={`medium-input${model.get('center_high') ? ' non-clickable' : ''}`}
                />{' '}
                <input
                  type="checkbox"
                  name="center_high"
                  className="model_field centered-checkbox"
                  checked={!!model.get('center_high')}
                  onChange={(e) => api.commitModelCheckbox('center_high', e.target.checked)}
                />{' '}
                <span className="checkbox-label">Center</span>{' '}
                <span className="label">Height from Floor</span>
              </div>
            </li>
            <li>
              <div className="long_unit">
                <span className="unit">in</span>{' '}
                <span className="selection">
                  {matchedSize ? <span>{String(matchedSize.wide_high ?? '')}</span> : null}
                </span>{' '}
                <select
                  name="size_and_qty_id"
                  className="eliminator_calculated_field model_field"
                  value={matchedSize ? String(matchedSize.id) : 'None'}
                  onChange={(e) => api.commitModelField('size_and_qty_id', e.target.value)}
                >
                  <option value="None" className="hide">
                    -- x --
                  </option>
                  {sizeAndQty.map((o) => (
                    <option key={String(o.id)} value={String(o.id)}>
                      {String(o.wide_high ?? '')}
                    </option>
                  ))}
                </select>{' '}
                <span className="label">
                  <span className="required">Qty Wide</span>{' '}
                  <span className="required">x High</span>
                </span>
              </div>
            </li>
            <li>
              <div className="convention single-line">
                <MBareSelect api={api} name="convention_left_right_id" cmsPath="convention_left_right" extraClass="medium-input" selectionClass="medium-selection" noneMode="visible" />{' '}
                <MBareSelect api={api} name="convention_bottom_top_id" cmsPath="convention_bottom_top" extraClass="medium-input" selectionClass="medium-selection" noneMode="visible" />{' '}
                <span className="label">Convention</span>
              </div>
            </li>
            <li>
              <div className="single-line">
                <span className="unit">ea</span>{' '}
                <MBareInput api={api} name="qty_24_24" fieldClass="small-first" formatter="integer" readOnly />{' '}
                <MBareInput api={api} name="qty_12_24" fieldClass="small-input" formatter="integer" readOnly />{' '}
                <MBareInput api={api} name="qty_24_12" fieldClass="small-input" formatter="integer" readOnly />{' '}
                <span className="label">WxH 24x24 | 12x24 | 24x12</span>
              </div>
            </li>
            <li>
              <div className="eliminator-supplied-sets single-line">
                <MBareSelect api={api} name="supplied_by_id" cmsPath="supplied_by" extraClass="medium-input" selectionClass="medium-selection" noneMode="visible" />{' '}
                <MBareInput api={api} name="qty_of_sets" formatter="integer" extraClass="small-input" />{' '}
                <span className="label">
                  <span>Supplied By </span> | <span>Sets</span>
                </span>
              </div>
            </li>
          </FormSubtab>

          {/* Dev-sync J6 — season split (eliminator form.tmpl +296..372):
              data-season on the sublist ul + the Season select + gated
              Summer/Winter EDB|EWB / LDB|LWB groups replace the old
              unsuffixed pair. LDB|LWB max is 240.00 here (silencer's is 150). */}
          <FormSubtab
            type="eliminator"
            cid={row.id}
            name="performance"
            label="Performance"
            open={sub('performance')}
            onToggle={api.toggleSubtab}
            sublistDataSeason={deriveSeasonValue(cms, section.season_id as number | null)}
          >
            <AirStreamFlow api={api} cms={cms} rowClass="first-two-line" />
            <PerformanceCommon api={api} typePrefix="eliminator" />
            <ComponentSeasonBlock
              api={api}
              section={section as Record<string, unknown>}
              cms={cms}
              component="Eliminator"
              typePrefix="eliminator"
              leavingMax={240}
            />
          </FormSubtab>

          <FormSubtab
            type="eliminator"
            cid={row.id}
            name="frame"
            label="Frame"
            open={sub('frame')}
            onToggle={api.toggleSubtab}
            liClass="group-with-include-checkbox"
            sublistClass={model.get('has_frame') ? ' active-group ' : undefined}
            headerChrome={
              <HeadingChrome
                api={api}
                spec={{
                  field: config.catalogueFields[1],
                  visClass: 'vis-frame',
                  searchResultClass: 'search-result',
                }}
                wrapperClass="ahu-header-btns right"
              />
            }
          >
            <li className="include-checkbox">
              <div>
                <div className="checkboxes-group">
                  <label>
                    <input
                      type="checkbox"
                      name="has_frame"
                      className="model_field"
                      checked={!!model.get('has_frame')}
                      onChange={(e) => api.commitModelCheckbox('has_frame', e.target.checked)}
                    />
                  </label>
                </div>{' '}
                <span className="label">Include Frame</span>
              </div>
            </li>
            <PSelect api={api} target="master_frame_id" name="frame_type_id" label="Type " required cmsPath="frame.type" />
            <PSelect api={api} target="master_frame_id" name="mfg_id" label="Manufacturer " required cmsPath="frame.manufacturer" />
            <PSelect api={api} target="master_frame_id" name="series_id" label="Series " required cmsPath="frame.series" labelKey="series" />
            <PSelect api={api} target="master_frame_id" name="mtl_id" label="Material" cmsPath="frame.mtl_gauge" />
          </FormSubtab>

          <BlankOffSlot api={api} sectionId={section.id} />
        </ul>
      </form>
      <RequiredFieldNote />
    </>
  );
};

export default EliminatorForm;
